import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import toast from 'react-hot-toast';
import { bookingService } from '../../services/bookingService';
import { roomService } from '../../services/roomService';
import { UI, ui, fieldStyles, buttonStyle } from '../../utils/ui';

const addDays = (date, n) => { const d = new Date(date); d.setDate(d.getDate() + n); return d; };
const fmt = (d) => d ? d.toISOString().split('T')[0] : '';

const CreateOfflineBooking = () => {
  const navigate = useNavigate();
  const [rooms, setRooms]       = useState([]);
  const [saving, setSaving]     = useState(false);
  const [form, setForm]         = useState({
    room_id:        '',
    check_in:       null,
    check_out:      null,
    guests:         1,
    customer_name:  '',
    customer_phone: '',
    payment_status: 'pending',
    special_requests: '',
  });

  useEffect(() => {
    roomService.getAllRooms().then(res => setRooms(res.data.rooms || [])).catch(() => {});
  }, []);

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const nights = () => {
    if (!form.check_in || !form.check_out) return 0;
    return Math.round((form.check_out - form.check_in) / (1000 * 60 * 60 * 24));
  };

  const selectedRoom = rooms.find(r => String(r.id) === String(form.room_id));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.room_id || !form.check_in || !form.check_out || !form.customer_name.trim()) {
      toast.error('Room, dates, and customer name are required.');
      return;
    }
    if (nights() < 1) {
      toast.error('Check-out must be after check-in.');
      return;
    }
    setSaving(true);
    try {
      await bookingService.createBooking({
        room_id:          Number(form.room_id),
        check_in:         fmt(form.check_in),
        check_out:        fmt(form.check_out),
        guests:           Number(form.guests),
        customer_name:    form.customer_name.trim(),
        customer_phone:   form.customer_phone.trim() || undefined,
        payment_status:   form.payment_status,
        special_requests: form.special_requests.trim() || undefined,
      });
      toast.success('Booking created successfully.');
      navigate('/owner/bookings');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create booking.');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = { ...fieldStyles.input, boxSizing: 'border-box' };

  return (
    <div style={{ maxWidth: 640, margin: '0 auto' }}>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontFamily: UI.font.heading, fontSize: 30, fontWeight: 900, margin: 0, color: UI.colors.text }}>New Offline Booking</h1>
        <p style={{ margin: '8px 0 0', fontSize: 14, color: UI.colors.muted }}>Manually enter a walk-in or phone booking</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={{ ...ui.card, padding: 28, display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Room */}
          <div>
            <label style={fieldStyles.label}>Room *</label>
            <select
              required
              value={form.room_id}
              onChange={e => set('room_id', e.target.value)}
              style={inputStyle}
            >
              <option value="">— Select a room —</option>
              {rooms.map(r => (
                <option key={r.id} value={r.id}>
                  {r.name} {r.room_number ? `(${r.room_number})` : ''} — ₹{Number(r.price_per_night).toLocaleString()}/night
                </option>
              ))}
            </select>
          </div>

          {/* Dates */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label style={fieldStyles.label}>Check-in *</label>
              <DatePicker
                selected={form.check_in}
                onChange={d => { set('check_in', d); if (form.check_out && d >= form.check_out) set('check_out', null); }}
                minDate={new Date()}
                dateFormat="dd MMM yyyy"
                placeholderText="Select date"
                customInput={<input style={inputStyle} />}
              />
            </div>
            <div>
              <label style={fieldStyles.label}>Check-out *</label>
              <DatePicker
                selected={form.check_out}
                onChange={d => set('check_out', d)}
                minDate={form.check_in ? addDays(form.check_in, 1) : addDays(new Date(), 1)}
                dateFormat="dd MMM yyyy"
                placeholderText="Select date"
                customInput={<input style={inputStyle} />}
              />
            </div>
          </div>

          {/* Pricing summary */}
          {selectedRoom && nights() > 0 && (
            <div style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '12px 16px', fontSize: 14 }}>
              <span style={{ color: UI.colors.muted }}>{nights()} night{nights() !== 1 ? 's' : ''} × ₹{Number(selectedRoom.price_per_night).toLocaleString()} = </span>
              <strong style={{ color: UI.colors.primary }}>₹{(nights() * Number(selectedRoom.price_per_night)).toLocaleString()}</strong>
              <span style={{ color: '#9ca3af', fontSize: 12, marginLeft: 6 }}>(date overrides applied at booking)</span>
            </div>
          )}

          {/* Guests */}
          <div>
            <label style={fieldStyles.label}>Guests *</label>
            <input
              type="number"
              min={1}
              max={selectedRoom?.capacity || 20}
              value={form.guests}
              onChange={e => set('guests', e.target.value)}
              style={inputStyle}
              required
            />
            {selectedRoom && <p style={fieldStyles.help}>Max capacity: {selectedRoom.capacity}</p>}
          </div>

          {/* Customer */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label style={fieldStyles.label}>Customer Name *</label>
              <input
                type="text"
                value={form.customer_name}
                onChange={e => set('customer_name', e.target.value)}
                placeholder="Walk-in guest name"
                style={inputStyle}
                required
              />
            </div>
            <div>
              <label style={fieldStyles.label}>Phone</label>
              <input
                type="tel"
                value={form.customer_phone}
                onChange={e => set('customer_phone', e.target.value)}
                placeholder="+91 98765 43210"
                style={inputStyle}
              />
            </div>
          </div>

          {/* Payment status */}
          <div>
            <label style={fieldStyles.label}>Payment Status</label>
            <select value={form.payment_status} onChange={e => set('payment_status', e.target.value)} style={inputStyle}>
              <option value="pending">Pending (collect later)</option>
              <option value="paid">Paid (cash / bank transfer received)</option>
            </select>
          </div>

          {/* Special requests */}
          <div>
            <label style={fieldStyles.label}>Special Requests</label>
            <textarea
              value={form.special_requests}
              onChange={e => set('special_requests', e.target.value)}
              rows={3}
              placeholder="Any special requirements..."
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', paddingTop: 8 }}>
            <button type="button" onClick={() => navigate('/owner/bookings')} style={buttonStyle('outline')}>
              Cancel
            </button>
            <button type="submit" disabled={saving} style={buttonStyle('primary')}>
              {saving ? 'Creating…' : 'Create Booking'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CreateOfflineBooking;
